import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JsonLd from "@/components/JsonLd";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SITE_URL, scrollToId } from "@/lib/api";

const FAQ_GROUPS = [
  {
    cat: "Ceny a dotácie",
    items: [
      {
        q: "Koľko stojí zateplenie rodinného domu?",
        a: "Orientačne približne 55 – 100 €/m² s DPH na kľúč, teda vrátane materiálu, práce (montáže), lešenia aj dopravy. Typický rodinný dom s plochou fasády 150 m² a grafitovým EPS 140 mm vyjde približne na 10 200 € s DPH. Konkrétna cena závisí od materiálu, hrúbky, typu muriva a rozsahu prác.",
        links: [{ to: "/blog/kolko-stoji-zateplenie-2026", label: "Cenová štúdia s tabuľkami" }],
      },
      {
        q: "Čo všetko je zahrnuté v cene zateplenia?",
        a: "Kompletný kontaktný zatepľovací systém ETICS: izolačné dosky (EPS / grafit / minerálna vata), lepiaca malta, kotvy (hmoždinky), armovacia vrstva so sklotextilnou sieťkou, penetrácia a finálna fasádna omietka. K tomu práca, montáž a demontáž lešenia a doprava materiálu. Cena z kalkulačky je preto cena na kľúč, nie len za materiál.",
      },
      {
        q: "Prečo sa ceny zateplenia na internetu tak líšia?",
        a: "Niektoré cenníky uvádzajú len materiál alebo len prácu, iné ceny bez DPH alebo bez lešenia. Porovnávajte vždy cenu na kľúč — teda vrátane lešenia, dopravy a DPH. Lacná ponuka bez lešenia a DPH vie byť v súčte drahšia.",
      },
      {
        q: "Zdražie zateplenie, ak počkám?",
        a: "Ceny stavebných materiálov a prác dlhodobo rastú — podľa nášho interného cenníka boli ceny vlani o cca 15 % a predvlani o cca 20 % nižšie ako dnes a na ďalší rok odhadujeme rast okolo 7 %. Pri typickom rodinnom dome robí rok čakania rozdiel rádovo v stovkách až tisíckach eur.",
        links: [{ to: "/blog/kolko-stoji-zateplenie-2026", label: "Vývoj cien zateplenia" }],
      },
      {
        q: "Aké dotácie môžem na zateplenie získať?",
        a: "Na obnovu a zateplenie rodinných domov je možné čerpať štátnu podporu z programu Obnov dom (Plán obnovy a odolnosti), ktorá dokáže pokryť významnú časť nákladov. Žiadosť sa spravidla podáva pred začatím prác — aktuálne podmienky a výšku príspevku odporúčame overiť na oficiálnej stránke programu.",
        links: [{ to: "/blog/dotacia-obnov-dom-2026-ako-ziskat-prispevok-na-zateplenie-rodinneho-do", label: "Návod na dotáciu Obnov dom" }],
      },
      {
        q: "Je obhliadka a cenová ponuka spoplatnená?",
        a: "Nie. Obhliadka aj cenová ponuka sú úplne zadarmo a nezáväzné. Rezerváciu obhliadky si viete spraviť online a termín kedykoľvek presunúť alebo zrušiť.",
      },
    ],
  },
  {
    cat: "Materiály a omietky",
    items: [
      {
        q: "Aký materiál je najlepší — polystyrén alebo minerálna vata?",
        a: "Fasádny EPS (polystyrén) je najdostupnejší a má výborný pomer cena/výkon. Grafitový EPS má lepší tepelný odpor pri menšej hrúbke. Minerálna vata je nehorľavá a paropriepustná — vhodná pri vyšších nárokoch na požiarnu bezpečnosť (napr. bytové domy) a pri difúzne otvorených konštrukciách. XPS sa používa najmä na sokel a miesta v kontakte so zemou.",
        links: [{ to: "/materialy", label: "Porovnanie materiálov" }],
      },
      {
        q: "EPS 70F alebo EPS 100S — aký polystyrén použiť na fasádu?",
        a: "Na fasádu sa lepí fasádny EPS 70F (práve na ňu je certifikovaný), nie stenový EPS 100S. EPS 100S je pevnejší v tlaku a hodí sa na podlahy a sokol; na fasáde je zbytočne drahý a ťažší na opracovanie. Pre lepšiu izoláciu pri rovnakej hrúbke zvoľte grafitový (šedý) EPS.",
      },
      {
        q: "Aký je rozdiel medzi bielym a grafitovým (šedým) EPS?",
        a: "Grafitový EPS obsahuje prísadu grafitu, vďaka ktorej má lepší súčiniteľ tepelnej vodivosti (λ ≈ 0,031 vs. 0,040 W/mK) — pri rovnakej hrúbke izoluje zhruba o 20 – 25 % lepšie. Je o niečo drahší, ale pri montáži treba dávať pozor na priame slnko (šedé dosky sa viac zahrievajú).",
      },
      {
        q: "Na čo slúži XPS a kde sa používa?",
        a: "XPS (extrudovaný polystyrén) je tvrdá, vodeodolná izolácia určená predovšetkým na sokel, spodnú časť fasády v kontakte so zemou a miesta vystavené vlhkosti a nárazom. Na bežnú plochu fasády je drahý a zbytočný — tam stačí EPS alebo minerálna vata.",
      },
      {
        q: "Akú omietku zvoliť na zateplenú fasádu?",
        a: "Najuniverzálnejšia je silikónová omietka (odolná voči vlhku, riasam a zašpineniu, ideálna na EPS). Silikátová je vhodná na minerálnu vatu a historické objekty. Akrylátová je cenovo dostupná, ale menej paropriepustná — na vatu sa neodporúča. Minerálna omietka je najtvrdšia a najlacnejšia, vyžaduje však náter.",
        links: [{ to: "/omietky", label: "Porovnanie omietok" }],
      },
      {
        q: "Akú farbu fasády vybrať?",
        a: "Pri fasádach na EPS treba dávať pozor na veľmi tmavé odtiene — čím tmavšia farba, tým viac sa fasáda na slnku zahrieva, čo môže spôsobiť praskliny či deformácie. Výrobcovia systémov preto na EPS limitujú najtmavšie povolené odtiene. Svoje kombinácie si viete bezplatne vyskúšať v našej AI vizualizácii.",
        links: [{ to: "/vizualizacia", label: "Vyskúšať farby na vlastnej fotke" }],
      },
    ],
  },
  {
    cat: "Hrúbka a normy",
    items: [
      {
        q: "Akú hrúbku zateplenia mám zvoliť?",
        a: "Najčastejšie 120 – 200 mm. Dnešný štandard pre rodinné domy je zvyčajne 160 – 200 mm — hrubšia izolácia znamená vyšší tepelný odpor a väčšiu úsporu, pričom rozdiel v cene medzi 140 a 180 mm je relatívne malý oproti dlhodobému prínosu.",
      },
      {
        q: "Aká hrúbka izolácie vyhovuje norme?",
        a: "Norma STN 73 0540 vyžaduje pre fasádu tepelný odpor R = 4,4 m²K/W (odporúčaná cieľová hodnota je 6,5). Pri bežnom tehlovom dome to znamená približne 140 mm bieleho EPS alebo 110 mm grafitového EPS. Presný výpočet závisí od skladby steny — orientačne si ho viete prepočítať na našej stránke noriem.",
        links: [{ to: "/normy", label: "Normy a interaktívny prepočet" }],
      },
      {
        q: "Oplatí sa hrúbka 200 mm?",
        a: "Spravidla áno. Príplatok za 200 mm namiesto 140 mm je pri EPS len niekoľko eur za m², pri dlhej vykurovacej sezóne sa však investícia vráti niekoľkonásobne a dom získa aj lepší letný tepelný komfort.",
      },
      {
        q: "Sú požiarné zábrany pri EPS povinné?",
        a: "Pri bytových domoch áno — nad každým podlažím s otvormi sa navrhujú pásy z nehorľavej minerálnej vlny široké aspoň 200 mm (požiarna norma STN 73 0802). Pri obnove rodinného domu do požiarnej výšky 22,5 m možno EPS spravidla použiť aj bez zábran; pri minerálnej vate ich netreba vôbec. Konečné posúdenie má vždy projektant.",
        links: [{ to: "/normy", label: "Viac o požiarnej bezpečnosti" }],
      },
    ],
  },
  {
    cat: "Priebeh a realizácia",
    items: [
      {
        q: "Ako prebieha zateplenie krok po kroku?",
        a: "1) Bezplatná obhliadka a cenová ponuka. 2) Montáž lešenia. 3) Lepenie a kotvenie izolačných dosiek. 4) Armovacia vrstva so sieťkou. 5) Penetračný náter. 6) Finálna fasádna omietka. 7) Demontáž lešenia a upratanie. Celý proces zastrešuje overený partner so zárukou.",
        links: [{ to: "/blog/postup-realizacie-zateplenia", label: "Postup realizácie krok po kroku" }],
      },
      {
        q: "Ako dlho trvá zateplenie domu?",
        a: "Zateplenie fasády rodinného domu trvá orientačne 2 – 4 týždne podľa veľkosti, rozsahu prác a počasia. Presný harmonogram dohodne realizačný partner po obhliadke.",
      },
      {
        q: "Kedy je najlepší čas na zatepľovanie?",
        a: "Ideálne od jari do jesene, keď sú teploty stabilne nad približne 5 °C a bez mrazu či dlhotrvajúceho dažďa. Lepiace malty a omietky potrebujú na správne vytvrdnutie vhodné podmienky.",
      },
      {
        q: "Dá sa zatepľovať v zime?",
        a: "Len obmedzene — lepiace malty a omietky potrebujú teploty nad približne 5 °C aj počas schnutia. V zime sa preto zateplenie bežne nerealizuje; využite ju radšej na prípravu, obhliadku a výber materiálov, aby ste na jar mali termín medzi prvými.",
      },
      {
        q: "Ako mám pripraviť dom pred zateplením?",
        a: "Pred prácami treba z fasády demontovať prekážky — odkvapy, klimatizačné jednotky, satelity, osvetlenie a markízy — a zabezpečiť prístup k fasáde (napr. skrátiť kryté nádoby či konáre). Drobné prídavné práce (predĺženie parapetov, nové okenné plechy) sa zvyčajne dohadujú pri obhliadke.",
      },
      {
        q: "Potrebujem na zateplenie povolenie?",
        a: "Pri bežnom zateplení rodinného domu bez zásahu do nosných konštrukcií sa spravidla stavebné povolenie nevyžaduje. Inak je to pri pamiatkovo chránených objektoch, v pamiatkových zónach a pri zložitejších stavebných zásahoch — odporúčame krátku konzultáciu so stavebným úradom.",
      },
      {
        q: "Kto bude zateplenie realizovať?",
        a: "V každom okrese na Slovensku máme overených realizačných partnerov s referenciami. Po výpočte ceny vás spojíme s partnerom vo vašom okrese — dopyt aj komunikácia prebiehajú cez platformu.",
      },
    ],
  },
  {
    cat: "Technické otázky",
    items: [
      {
        q: "Čo je rosný bod a prečo na ňom záleží?",
        a: "Rosný bod je miesto v konštrukcii, kde sa vodná para mení na vodu. Pri správne navrhnutom zateplení sa posúva do izolantu, kde neškodí; pri zlej skladbe môže vzniknúť vo vnútri steny a spôsobiť vlhnutie a plesne. Preto sa hrúbka a materiál navrhujú podľa skladby steny, nie „od oka“.",
        links: [{ to: "/blog/pozor-na-rosny-bod-pri-zatepleni", label: "Pozor na rosný bod" }],
      },
      {
        q: "Pomôže zateplenie proti plesniam a vlhkosti?",
        a: "Často áno — plesne v rohoch miestností vznikajú najmä tam, kde je studený povrch steny. Zateplenie povrch stien zohreje nad kritickú teplotu a plesniam zamedzí podmienky na rast. Ak je však príčinou vzlínajúca vlhkosť zo základov, treba riešiť najprv sanáciu muriva.",
      },
      {
        q: "Ako sa zatepľuje sokel?",
        a: "Sokel (spodná časť fasády nad terénom) sa zatepľuje odolným XPS, ktorý znesie vlhkosť, mráz aj nárazy. Naň sa aplikuje silikónová alebo minerálna omietka, prípadne obklad. Hrana medzi soklom a fasádou sa čistí zakladacou lištou.",
      },
      {
        q: "Čo sú tepelné mosty a ako ich zateplenie rieši?",
        a: "Tepelný most je miesto, kde teplo uniká rýchlejšie (rohy, preklady, styk steny so stropom, osadenie okien). Kontaktné zateplenie fasády väčšinu tepelných mostov na obvode domu odstráni; kritické zostávajú najmä okolo okien — preto sa parapety a ostenia riešia detailne.",
      },
      {
        q: "Dá sa zatepliť pórobetón alebo drevostavba?",
        a: "Áno, ale s rešpektom k materiálu. Pórobetón je mäkký a vyžaduje špeciálne dlhé kotvy a opatrnú montáž. Drevostavba potrebuje paropriepustné riešenie — typicky minerálnu vatu so silikátovou omietkou, aby konštrukcia mohla „dýchať“.",
      },
      {
        q: "Ako sa zatepľuje bytový dom (panelák)?",
        a: "Bytový dom zatepľujú vlastníci bytov spoločne — schválenie prebieha na schôdzi a financovanie zvyčajne z fondu opráv, prípadne kombinovane s úverom či dotáciou. Pri EPS sú povinné požiarné zábrany z minerálnej vlny. Výhodou bytoviek je nižšia cena za m² vďaka veľkej ploche.",
      },
    ],
  },
  {
    cat: "Po zateplení",
    items: [
      {
        q: "Koľko ušetrím na kúrení po zateplení?",
        a: "Typicky 30 – 40 % nákladov na vykurovanie. Skutočná úspora závisí od pôvodného stavu domu, hrúbky izolácie a spôsobu vykurovania. Okrem úspory získate výrazne vyšší komfort — v zime teplo dlhšie vydrží a v lete sa dom tak neprehrieva.",
      },
      {
        q: "Akú záruku dostanem?",
        a: "36 mesiacov záruka na prácu (montáž) a 60 mesiacov na materiál zatepľovacieho systému. Realizáciu vykonáva preverený partner s referenciami.",
      },
      {
        q: "Ako sa o zateplenú fasádu starať?",
        a: "Zateplená fasáda je takmer bezúdržbová — stačí ju raz za pár rokov umyť nízkotlakovou vodou a skontrolovať tmely a napojenia. Pri silikónovej omietke odpadá aj časté premaľovávanie. Drobné poškodenie omietky je dobré opraviť čím skôr, aby sa do systému nedostala voda.",
      },
      {
        q: "Ponúkate vizualizáciu novej fasády?",
        a: "Áno. Z fotky vášho domu vytvoríme bezplatnú AI vizualizáciu — 3 návrhy novej zateplenej fasády s rôznymi farbami a povrchmi, aby ste videli výsledok ešte pred realizáciou.",
        links: [{ to: "/vizualizacia", label: "Vyskúšať AI vizualizáciu" }],
      },
    ],
  },
];

const FAQ = FAQ_GROUPS.flatMap((g) => g.items);

export default function Faq() {
  useEffect(() => {
    document.title = "Časté otázky o zateplení — Zateplenia.eu";
    const m = document.querySelector('meta[name="description"]');
    const prev = m?.getAttribute("content");
    if (m) m.setAttribute("content", "Odpovede na najčastejšie otázky o zateplení fasády: cena, materiály, hrúbka, normy, dotácie, priebeh realizácie, záruka a úspora na kúrení. Zateplenia.eu.");
    window.scrollTo(0, 0);
    return () => { if (m && prev) m.setAttribute("content", prev); };
  }, []);

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${SITE_URL}/faq#faqpage`,
    "mainEntity": FAQ.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  let idx = -1;

  return (
    <div className="bg-ink min-h-screen">
      <Header />
      <JsonLd id="ld-faq-page" data={faqLd} />
      <section className="max-w-[900px] mx-auto px-5 md:px-10 pt-32 pb-24" data-testid="faq-page">
        <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Časté otázky · {FAQ.length} odpovedí</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] text-white">
          Všetko o <span className="text-gold">zateplení</span><br />na jednom mieste
        </h1>
        <p className="text-white/55 text-sm md:text-base mt-5 max-w-2xl">
          Cena, materiály, hrúbka, normy, dotácie, priebeh realizácie aj záruka — prehľadné odborné odpovede
          na otázky o zateplení fasády rodinných a bytových domov na Slovensku.
        </p>

        {FAQ_GROUPS.map((g) => (
          <div key={g.cat} className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-2 flex items-center gap-3">
              <span className="w-6 h-px bg-gold inline-block" />{g.cat}
            </h2>
            <Accordion type="single" collapsible data-testid={`faq-group-${g.cat}`}>
              {g.items.map((f) => {
                idx += 1;
                const i = idx;
                return (
                  <AccordionItem key={i} value={`q-${i}`} className="border-white/10" data-testid={`faq-q-${i}`}>
                    <AccordionTrigger className="text-white font-semibold text-base md:text-lg leading-snug text-left hover:no-underline hover:text-gold [&[data-state=open]]:text-gold py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent forceMount hideWhenClosed className="text-white/60 text-sm md:text-base leading-relaxed pr-4">
                      {f.a}
                      {f.links && (
                        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`faq-related-${i}`}>
                          <span className="text-white/35 text-xs uppercase tracking-widest font-mono2">Čítajte viac:</span>
                          {f.links.map((r) => (
                            <Link key={r.to} to={r.to} className="inline-flex items-center gap-1 text-gold text-sm hover:underline">
                              {r.label} <ArrowRight className="w-3 h-3" />
                            </Link>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        ))}

        {/* CTA */}
        <div className="mt-14 border border-gold/30 bg-gold/5 p-8 md:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="faq-cta">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-white">Zistite cenu pre váš dom</h2>
            <p className="text-white/60 mt-2 text-sm md:text-base">Bezplatná kalkulačka — výsledok do 60 sekúnd, bez záväzkov.</p>
          </div>
          <Link
            to="/#kalkulacka"
            onClick={() => setTimeout(() => scrollToId("#kalkulacka"), 60)}
            data-testid="faq-cta-btn"
            className="shrink-0 bg-gold text-ink px-8 py-4 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors flex items-center gap-2"
          >
            Vypočítať cenu <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
